import React, { memo, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import _cloneDeep from "lodash/cloneDeep";
import {
  DashboardApiV2DashboardSubstancesGetRequest,
  DashboardSubstances,
} from "@generated/v2";
import { useFetchData } from "app/hooks/hooks";
import { dashboardSubstancesGet } from "app/api/v2/dashboardApi";
import { DashboardItemFilterComplete } from "app/types/dashboardType";
import { ChartColorsEnum } from "app/constants/dashboardConst";
import DashboardItemLayout from "../DashboardItemLayout/DashboardItemLayout";
import DashboardChart, {
  ChartData,
  ChartDataSets,
} from "../DashboardChart/DashboardChart";
import ErrorFallbackUI from "app/components/ErrorFallbackUI";
import { SwitchProps } from "app/components/Common/Switch";
import { ChartTestFlowToggleLabel } from "app/screens/DashboardScreen/components/common/ChartTestFlowToggleLabel";

const ChartSubstances = ({
  customerId,
  siteId,
  start,
  end,
  routes,
  period,
}: DashboardItemFilterComplete) => {
  const { t } = useTranslation();

  const [showPositive, setShowPositive] = useState(true);
  const [showNegative, setShowNegative] = useState(true);

  const { data, loading } = useFetchData<
    DashboardSubstances,
    DashboardApiV2DashboardSubstancesGetRequest
  >(dashboardSubstancesGet, {
    shouldCallApi: !!customerId,
    params: {
      customerId,
      siteId,
      start,
      end,
      routes,
    },
  });

  const chartData = useMemo<ChartData>(() => {
    const dataClone = _cloneDeep(data);
    let datasets: ChartDataSets[] = [];

    const negativeCount = {
      label: t("dashboard.datasetLabelNegative"),
      data: dataClone?.negativeCount ?? undefined,
      backgroundColor: ChartColorsEnum.BLUE,
      maxBarThickness: 24,
    };

    const positiveCount = {
      label: t("dashboard.datasetLabelPositive"),
      data: dataClone?.positiveCount ?? undefined,
      backgroundColor: ChartColorsEnum.RED,
      maxBarThickness: 24,
    };

    if (showNegative) {
      datasets.push(negativeCount);
    }

    if (showPositive) {
      datasets.push(positiveCount);
    }

    return { datasets, labels: dataClone?.substances ?? undefined };
  }, [data, t, showNegative, showPositive]);

  const togglePositive = useCallback(() => {
    setShowPositive(prevState => !prevState);
  }, []);

  const toggleNegative = useCallback(() => {
    setShowNegative(prevState => !prevState);
  }, []);

  const switches = useMemo<SwitchProps[]>(
    () => [
      {
        checked: showPositive,
        onChange: togglePositive,
        label: (
          <ChartTestFlowToggleLabel
            i18nKey="dashboard.checkboxShowPositiveWithBox"
            positive
          />
        ),
      },
      {
        checked: showNegative,
        onChange: toggleNegative,

        label: (
          <ChartTestFlowToggleLabel
            i18nKey="dashboard.checkboxShowNegativeWithBox"
            positive={false}
          />
        ),
      },
    ],
    [showNegative, showPositive, toggleNegative, togglePositive]
  );

  return (
    <ErrorFallbackUI>
      <DashboardItemLayout
        background
        title={t("dashboard.substancesTitle")}
        subTitle={period}
        loading={loading}
        info={t("dashboard.substancesInfo")}
        switches={switches}
      >
        <DashboardChart type="horizontalBar" stacked data={chartData} />
      </DashboardItemLayout>
    </ErrorFallbackUI>
  );
};

export default memo(ChartSubstances);
